import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  AlarmClock,
  BanknoteArrowUp,
  BellRing,
  Coins,
  DatabaseBackup,
  Gift,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Repeat,
  ScrollText,
  Sun,
  Users,
  X,
} from 'lucide-react';
import { LANGUAGES, useI18n, useT } from '../lib/i18n';
import { useLogout, type Session } from '../lib/session';
import { useTheme } from '../lib/theme';
import { Button, cn } from './ui';

/**
 * One nav definition drives the sidebar and the drawer, so the two cannot
 * drift apart. The order is the order an operator works in: what happened,
 * who it happened to, what they set, what it paid, what we told them, and who
 * changed what — and, last, whether all of it is safely backed up.
 *
 * Events are deliberately absent. They are only legible next to the alarm that
 * produced them, so they are reached from an alarm rather than from a log of
 * everything that has ever happened.
 */
const NAV_ITEMS = [
  { to: '/', label: 'nav.dashboard', icon: LayoutDashboard, end: true },
  { to: '/users', label: 'nav.users', icon: Users, end: false },
  { to: '/alarms', label: 'nav.alarms', icon: AlarmClock, end: false },
  { to: '/economy', label: 'nav.rewards', icon: Coins, end: false },
  { to: '/withdrawals', label: 'nav.withdrawals', icon: BanknoteArrowUp, end: false },
  { to: '/conversions', label: 'nav.conversions', icon: Repeat, end: false },
  { to: '/referrals', label: 'nav.referrals', icon: Gift, end: false },
  { to: '/notifications', label: 'nav.notifications', icon: BellRing, end: false },
  { to: '/audit', label: 'nav.activity', icon: ScrollText, end: false },
  { to: '/backups', label: 'nav.backups', icon: DatabaseBackup, end: false },
];

/**
 * The app shell: a persistent sidebar from `lg` up, and a slide-over drawer
 * below it.
 */
export function Layout({ session }: { session: Session }) {
  const t = useT();
  const [navOpen, setNavOpen] = useState(false);
  const location = useLocation();

  // A tap that navigates should also dismiss the drawer it was tapped in.
  useEffect(() => setNavOpen(false), [location.pathname]);

  return (
    <div className="min-h-dvh bg-canvas">
      <MobileTopBar onOpenNav={() => setNavOpen(true)} />

      {navOpen && (
        <button
          type="button"
          aria-label={t('nav.close')}
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-30 bg-ink/40 backdrop-blur-[2px] lg:hidden"
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-line bg-surface',
          'transition-transform duration-200 lg:translate-x-0',
          navOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex items-center justify-between px-5 py-4">
          <Brand />
          <button
            type="button"
            onClick={() => setNavOpen(false)}
            aria-label={t('nav.close')}
            className="text-ink-2 lg:hidden"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 space-y-1 overflow-y-auto px-3 py-2">
          {NAV_ITEMS.map(({ to, label, icon: Icon, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
                  isActive
                    ? 'bg-brand-soft text-brand'
                    : 'text-ink-2 hover:bg-raised hover:text-ink',
                )
              }
            >
              <Icon size={18} />
              {t(label)}
            </NavLink>
          ))}
        </nav>

        <SidebarFooter username={session.username} />
      </aside>

      <main className="lg:pl-64">
        <div className="mx-auto max-w-[1500px] px-4 py-5 sm:px-6 sm:py-7">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function Brand() {
  const t = useT();

  return (
    <div className="flex items-center gap-2.5">
      <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand text-on-brand">
        <AlarmClock size={17} />
      </span>
      <span className="text-sm font-semibold tracking-tight text-ink">
        {t('nav.brand')}
      </span>
    </div>
  );
}

function MobileTopBar({ onOpenNav }: { onOpenNav: () => void }) {
  const t = useT();

  return (
    <header className="sticky top-0 z-20 flex items-center justify-between border-b border-line bg-surface/95 px-4 py-3 backdrop-blur lg:hidden">
      <button
        type="button"
        onClick={onOpenNav}
        aria-label={t('nav.open')}
        className="text-ink-2"
      >
        <Menu size={22} />
      </button>
      <Brand />
      {/* Balances the menu button so the brand stays optically centred. */}
      <span className="w-[22px]" aria-hidden />
    </header>
  );
}

function SidebarFooter({ username }: { username: string }) {
  const { theme, toggle } = useTheme();
  const t = useT();
  const logout = useLogout();

  return (
    <div className="space-y-2 border-t border-line px-3 py-3">
      <div className="px-2 text-xs text-ink-3">
        {t('nav.signedInAs')} <span className="text-ink-2">{username}</span>
      </div>
      <LanguagePicker />
      <div className="flex gap-2">
        <Button
          variant="ghost"
          onClick={toggle}
          className="flex-1"
          icon={theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
        >
          {theme === 'dark' ? t('nav.light') : t('nav.dark')}
        </Button>
        <Button
          variant="ghost"
          onClick={() => logout.mutate()}
          loading={logout.isPending}
          className="flex-1"
          icon={<LogOut size={16} />}
        >
          {t('nav.signOut')}
        </Button>
      </div>
    </div>
  );
}

/**
 * A two-option segmented control rather than a select: with exactly two
 * languages, the choice and the current state fit in the same row, and it
 * stays a single tap on a phone.
 */
function LanguagePicker() {
  const { language, setLanguage, t } = useI18n();

  return (
    <div
      role="group"
      aria-label={t('nav.language')}
      className="flex gap-1 rounded-lg border border-line p-1"
    >
      {LANGUAGES.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={language === option.value}
          onClick={() => setLanguage(option.value)}
          className={cn(
            'flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors',
            language === option.value
              ? 'bg-brand-soft text-brand'
              : 'text-ink-2 hover:bg-raised hover:text-ink',
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

/** Page title block, used at the top of every route. */
export function PageHeader({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-ink">
          {title}
        </h1>
        {description && (
          <p className="mt-1 text-sm text-ink-2">{description}</p>
        )}
      </div>
      {action}
    </div>
  );
}
